import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NAV_ITEMS, type NavKey } from "../components/dashboard";

export interface DashboardNavigationState {
  activeView: NavKey;
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  handleNavClick: (key: NavKey) => void;
}

export function useDashboardNavigation(): DashboardNavigationState {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const activeView: NavKey = useMemo(() => {
    const path = location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
    const routableItems = NAV_ITEMS.filter(
      (item) => !("groupHeader" in item && item.groupHeader),
    );
    const matchingKey = routableItems.find(
      (item) => path === item.key || path.startsWith(`${item.key}/`),
    )?.key;

    return matchingKey ?? "overview";
  }, [location.pathname]);

  const handleNavClick = useCallback(
    (key: NavKey) => {
      const item = NAV_ITEMS.find((candidate) => candidate.key === key);
      if (item && "groupHeader" in item && item.groupHeader) {
        return;
      }
      void navigate(`/${key}`);
      setIsMobileMenuOpen(false);
    },
    [navigate],
  );

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((previous) => !previous);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  return {
    activeView,
    closeMobileMenu,
    handleNavClick,
    isMobileMenuOpen,
    toggleMobileMenu,
  };
}
